import { useState } from 'react';
import { ArrowLeftIcon, SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import type { BoxCoordinates } from '@/types';
import type { EmptyVariant } from '@/lib/tryout/tryoutRun';
import { PrimaryButton, SectionLabel } from './TryoutStep1Upload';
import { TryoutVariantPicker } from './TryoutVariantPicker';
import { TryoutRefinePanel } from './TryoutRefinePanel';
import { TryoutGeneratingStage } from './TryoutGeneratingStage';

// ============================================================================
// Types — still owned here so TryoutView / TryoutStep3Results can import them
// without pulling in the whole module.
// ============================================================================

export type AspectRatio = '1:1' | '4:3';
export type StagingStyle = 'modern' | 'luxury' | 'industrial';

// ============================================================================
// Step 2 — Leerräumen. The emptying auto-starts on entry (kicked off by
// TryoutView), so this step has no upfront config: it shows the generating
// animation, then the A/B pick + optional refine. An error drops to a retry.
// ============================================================================

interface TryoutStep2ConfigureProps {
  previewUrl: string;
  aspectRatio: AspectRatio;
  hasGenerated: boolean;
  isGenerating: boolean;
  isFinishing: boolean;
  canGenerate: boolean;
  emptyVariants: Array<{ variant: EmptyVariant; url: string }>;
  selectedVariant: EmptyVariant | null;
  refinesLeft: number;
  isRefining: boolean;
  generationError: string | null;
  refineError: string | null;
  onGenerateEmpty: () => void;
  onSelectVariant: (v: EmptyVariant) => void;
  onRefine: (userPrompt: string, box?: BoxCoordinates) => void;
  onNext: () => void;
  onBack: () => void;
}

export const TryoutStep2Configure: React.FC<TryoutStep2ConfigureProps> = ({
  previewUrl,
  aspectRatio,
  hasGenerated,
  isGenerating,
  isFinishing,
  canGenerate,
  emptyVariants,
  selectedVariant,
  refinesLeft,
  isRefining,
  generationError,
  refineError,
  onGenerateEmpty,
  onSelectVariant,
  onRefine,
  onNext,
  onBack,
}) => {
  const selectedUrl = emptyVariants.find((v) => v.variant === selectedVariant)?.url ?? previewUrl;

  // The reprompt UI stays hidden behind a button until the visitor opts in —
  // keeps the choose-a-result step short and unambiguous.
  const [refineOpen, setRefineOpen] = useState(false);
  const noRefinesLeft = refinesLeft <= 0;

  return (
    <div className="space-y-6" style={{ fontFamily: "'Outfit Variable', 'Outfit', sans-serif" }}>
      <button
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#5F6B70] hover:text-[#001116] transition-colors -mt-2"
      >
        <ArrowLeftIcon className="w-4 h-4" strokeWidth={2} />
        Zurück
      </button>

      {!hasGenerated ? (
        !canGenerate && !isGenerating ? (
          // ── Free tries used up before emptying could run ───────────────────
          <div className="rounded-[20px] border border-[#EBEBE6] bg-[#FCFAF5] p-8 text-center space-y-3">
            <p className="text-[15px] font-semibold text-[#001116]">Keine kostenlosen Versuche mehr übrig</p>
            <p className="text-[13px] text-[#5F6B70]">Registriere dich für unbegrenzte Nutzung.</p>
            <button
              type="button"
              onClick={onBack}
              className="text-[13px] font-medium text-[#5F6B70] hover:text-[#001116] transition-colors"
            >
              Zurück
            </button>
          </div>
        ) : generationError && !isGenerating ? (
          // ── Emptying failed → let the visitor retry (no token spent on error) ─
          <div className="rounded-[20px] border border-[#EBEBE6] bg-[#FCFAF5] p-8 text-center space-y-4">
            <p className="text-[14px] text-[#B91C1C]">{generationError}</p>
            <p className="text-[12px] text-[#9CA3AF]">Es wurde kein Versuch verbraucht.</p>
            <PrimaryButton
              onClick={onGenerateEmpty}
              trailingIcon={<SparklesIcon className="w-5 h-5" strokeWidth={1.75} />}
            >
              Erneut versuchen
            </PrimaryButton>
          </div>
        ) : (
          // ── Emptying runs automatically — keep the visitor engaged ──────────
          <TryoutGeneratingStage imageUrl={previewUrl} aspectRatio={aspectRatio} finished={isFinishing} />
        )
      ) : (
        // ── After: original + emptied options side by side; reprompt tucked away ─
        <div className="space-y-4">
          {!refineOpen ? (
            <>
              <div>
                <SectionLabel>Wähle das leergeräumte Bild</SectionLabel>
                <TryoutVariantPicker
                  variants={emptyVariants}
                  selected={selectedVariant}
                  onSelect={onSelectVariant}
                  aspectRatio={aspectRatio}
                  beforeUrl={previewUrl}
                />
              </div>

              {selectedVariant && (
                <button
                  type="button"
                  onClick={() => setRefineOpen(true)}
                  disabled={noRefinesLeft}
                  className={cn(
                    'w-full flex items-center justify-between gap-2 h-12 px-4 rounded-[12px] border transition-all duration-150',
                    noRefinesLeft
                      ? 'bg-[#F5F4F0] border-[#EBEBE6] text-[#9CA3AF] cursor-not-allowed'
                      : 'bg-white border-[#EBEBE6] text-[#001116] hover:border-[#FE6F17]/60 hover:bg-[#FCFAF5]',
                  )}
                >
                  <span className="inline-flex items-center gap-2 text-[14px] font-semibold">
                    <SparklesIcon className="w-[18px] h-[18px] text-[#FE6F17]" strokeWidth={1.75} />
                    Noch etwas anpassen?
                  </span>
                  <span className="text-[12px] font-medium text-[#9CA3AF]">
                    {noRefinesLeft ? 'Keine übrig' : `Noch ${refinesLeft} Anpassung${refinesLeft === 1 ? '' : 'en'}`}
                  </span>
                </button>
              )}
            </>
          ) : (
            <div className="tryout-reveal space-y-3">
              <div className="flex items-center justify-between">
                <SectionLabel icon={<SparklesIcon className="w-[18px] h-[18px]" strokeWidth={1.5} />}>
                  Bild anpassen
                </SectionLabel>
                <button
                  type="button"
                  onClick={() => setRefineOpen(false)}
                  className="text-[13px] font-medium text-[#5F6B70] hover:text-[#001116] transition-colors"
                >
                  Abbrechen
                </button>
              </div>
              <TryoutRefinePanel
                imageUrl={selectedUrl}
                refinesLeft={refinesLeft}
                isRefining={isRefining}
                error={refineError}
                onSubmit={onRefine}
              />
            </div>
          )}

          <PrimaryButton
            onClick={onNext}
            disabled={!selectedVariant}
            trailingIcon={<ArrowRightIcon className="w-5 h-5" strokeWidth={2} />}
          >
            Weiter zum Einrichten
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};
